// frontend/src/components/analytics/AnalyticsPage.js
// Analytics — Overview / Departments / Staff tabs over /complaints/metrics.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Tabs,
  Tab,
  Card,
  CardContent,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableSortLabel
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import api from '../../services/api';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Fixed color assignments — colors follow the entity, never its position.
const PRIORITY_COLORS = {
  Low: '#9e9e9e',
  Medium: '#42a5f5',
  High: '#ff9800',
  Urgent: '#f44336'
};

const STATUS_COLORS = {
  New: '#42a5f5',
  Open: '#9c27b0',
  Pending: '#ff9800',
  Solved: '#26a69a',
  Resolved: '#4caf50'
};

const FALLBACK_PALETTE = ['#42a5f5', '#7e57c2', '#66bb6a', '#ff7043', '#26c6da', '#ab47bc', '#ffa726', '#8d6e63'];

const KpiCard = ({ title, value, color = 'primary.main' }) => (
  <Card sx={{ height: '100%', boxShadow: 2, borderRadius: 2 }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const AnalyticsPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  const [dateRange, setDateRange] = useState('all');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  // Departments table sort state
  const [deptSortBy, setDeptSortBy] = useState('total');
  const [deptSortDir, setDeptSortDir] = useState('desc');

  const fetchMetrics = useCallback(async () => {
    // For custom range, wait until both dates are picked.
    if (dateRange === 'custom' && (!customStart || !customEnd)) return;
    setLoading(true);
    try {
      const params = {};
      if (dateRange !== 'all') {
        params.dateRange = dateRange;
        if (dateRange === 'custom') {
          params.start = customStart.format('YYYY-MM-DD');
          params.end = customEnd.format('YYYY-MM-DD');
        }
      }
      const res = await api.get('/complaints/metrics', { params });
      setMetrics(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError('Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, customStart, customEnd]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // ---------- Derived datasets ----------
  const priorityData = useMemo(() => {
    const stats = Array.isArray(metrics?.priorityStats) ? metrics.priorityStats : [];
    const order = ['Low', 'Medium', 'High', 'Urgent'];
    return stats
      .map((p) => ({
        name: p?._id || 'Medium',
        value: p?.count || 0,
        color: PRIORITY_COLORS[p?._id] || PRIORITY_COLORS.Medium
      }))
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  }, [metrics]);

  const statusData = useMemo(() => {
    const stats = Array.isArray(metrics?.statusCounts) ? metrics.statusCounts : [];
    return stats.map((s, idx) => ({
      name: s?._id || 'Unknown',
      count: s?.count || 0,
      color: STATUS_COLORS[s?._id] || FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length]
    }));
  }, [metrics]);

  const monthlyData = useMemo(() => {
    const stats = Array.isArray(metrics?.monthlyStats) ? metrics.monthlyStats : [];
    return stats.map((m) => ({
      month: m?._id
        ? `${MONTH_NAMES[(m._id.month || 1) - 1]} ${m._id.year || ''}`.trim()
        : 'N/A',
      complaints: m?.count || 0
    }));
  }, [metrics]);

  const categoryPerformance = useMemo(
    () => (Array.isArray(metrics?.categoryPerformance) ? metrics.categoryPerformance : []),
    [metrics]
  );

  const sortedCategoryPerformance = useMemo(() => {
    const rows = categoryPerformance.slice();
    rows.sort((a, b) => {
      const av = a?.[deptSortBy];
      const bv = b?.[deptSortBy];
      if (typeof av === 'string' || typeof bv === 'string') {
        const cmp = String(av || '').localeCompare(String(bv || ''));
        return deptSortDir === 'asc' ? cmp : -cmp;
      }
      const cmp = (av || 0) - (bv || 0);
      return deptSortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [categoryPerformance, deptSortBy, deptSortDir]);

  const topOpenCategories = useMemo(
    () =>
      categoryPerformance
        .slice()
        .sort((a, b) => (b?.open || 0) - (a?.open || 0))
        .slice(0, 10)
        .map((c) => ({ name: c?.category || 'Unknown', open: c?.open || 0 })),
    [categoryPerformance]
  );

  const staffAssigned = useMemo(
    () => (Array.isArray(metrics?.staffWorkloadAssigned) ? metrics.staffWorkloadAssigned : []),
    [metrics]
  );
  const staffCreated = useMemo(
    () => (Array.isArray(metrics?.staffWorkloadCreated) ? metrics.staffWorkloadCreated : []),
    [metrics]
  );

  const handleDeptSort = (field) => {
    if (deptSortBy === field) {
      setDeptSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setDeptSortBy(field);
      setDeptSortDir(field === 'category' ? 'asc' : 'desc');
    }
  };

  const avgResolutionDays =
    metrics?.resolutionStats?.avgDays !== undefined && metrics?.resolutionStats?.avgDays !== null
      ? Number(metrics.resolutionStats.avgDays).toFixed(1)
      : '–';
  const reopenRate =
    metrics?.reopenStats?.reopenRate !== undefined && metrics?.reopenStats?.reopenRate !== null
      ? `${Number(metrics.reopenStats.reopenRate).toFixed(1)}%`
      : '–';

  const deptColumns = [
    { field: 'category', label: 'Category' },
    { field: 'total', label: 'Total' },
    { field: 'open', label: 'Open' },
    { field: 'closed', label: 'Closed' },
    { field: 'avgResolutionDays', label: 'Avg Resolution Days' },
    { field: 'avgOpenAgeDays', label: 'Avg Open Age Days' },
    { field: 'reopens', label: 'Reopens' }
  ];

  const numberOrDash = (v, digits = 1) =>
    v === null || v === undefined ? '–' : Number(v).toFixed(digits);

  const renderStaffSection = (title, data) => {
    const rows = Array.isArray(data) ? data : [];
    const chartData = rows
      .slice()
      .sort((a, b) => (b?.total || 0) - (a?.total || 0))
      .slice(0, 15)
      .map((s) => ({
        name: s?.name || 'Unknown',
        open: s?.open || 0,
        closed: s?.closed || 0
      }));
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 1, maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Open</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Closed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((s, idx) => (
                    <TableRow key={s?._id || s?.userId || idx}>
                      <TableCell>{s?.name || 'Unknown'}</TableCell>
                      <TableCell align="right">{s?.total || 0}</TableCell>
                      <TableCell align="right">{s?.open || 0}</TableCell>
                      <TableCell align="right">{s?.closed || 0}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, height: 420 }}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 11 }}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <RechartsTooltip />
                    <Legend verticalAlign="top" />
                    <Bar dataKey="open" name="Open" stackId="a" fill="#ff9800" />
                    <Bar dataKey="closed" name="Closed" stackId="a" fill="#4caf50" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    No data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth={false} sx={{ width: '100%', mt: 7, p: 2, bgcolor: 'var(--bg-color)' }}>
        {/* Header + date range selector */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2
          }}
        >
          <Typography variant="h5" sx={{ color: 'var(--text-color)', fontWeight: 600 }}>
            Analytics
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="analytics-range-label">Date Range</InputLabel>
              <Select
                labelId="analytics-range-label"
                label="Date Range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="7days">Last 7 Days</MenuItem>
                <MenuItem value="weekly">This Week</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            {dateRange === 'custom' && (
              <>
                <DatePicker
                  label="Start"
                  value={customStart}
                  onChange={setCustomStart}
                  slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                />
                <DatePicker
                  label="End"
                  value={customEnd}
                  onChange={setCustomEnd}
                  slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                />
              </>
            )}
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Overview" />
          <Tab label="Departments" />
          <Tab label="Staff" />
        </Tabs>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress aria-label="Loading analytics" />
          </Box>
        ) : (
          <>
            {/* ---------------- Overview ---------------- */}
            {tab === 0 && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={4} md={3} lg={true}>
                    <KpiCard title="Total" value={metrics?.totalComplaints ?? 0} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={true}>
                    <KpiCard title="New" value={metrics?.newComplaints ?? 0} color="#2196f3" />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={true}>
                    <KpiCard title="Open" value={metrics?.openComplaints ?? 0} color="#9c27b0" />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={true}>
                    <KpiCard title="Pending" value={metrics?.pendingComplaints ?? 0} color="#ff9800" />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={true}>
                    <KpiCard title="Resolved" value={metrics?.resolvedComplaints ?? 0} color="#4caf50" />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={true}>
                    <KpiCard title="Avg Resolution (days)" value={avgResolutionDays} color="#26a69a" />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={true}>
                    <KpiCard title="Reopen Rate" value={reopenRate} color="#f44336" />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} lg={4}>
                    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, height: 380 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Complaints by Priority
                      </Typography>
                      {priorityData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="90%">
                          <PieChart>
                            <Pie
                              data={priorityData}
                              cx="50%"
                              cy="50%"
                              outerRadius={95}
                              innerRadius={45}
                              dataKey="value"
                              nameKey="name"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              labelLine={false}
                            >
                              {priorityData.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} stroke="#fff" strokeWidth={2} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                            <Legend iconType="circle" />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, height: 380 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Complaints by Status
                      </Typography>
                      {statusData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="90%">
                          <BarChart data={statusData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                            <RechartsTooltip />
                            <Bar dataKey="count" name="Complaints" radius={[4, 4, 0, 0]}>
                              {statusData.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, height: 380 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Monthly Trend
                      </Typography>
                      {monthlyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="90%">
                          <LineChart data={monthlyData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                            <RechartsTooltip />
                            <Line
                              type="monotone"
                              dataKey="complaints"
                              name="Complaints"
                              stroke="#42a5f5"
                              strokeWidth={2}
                              dot={{ r: 4, fill: '#42a5f5' }}
                              activeDot={{ r: 7 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ---------------- Departments ---------------- */}
            {tab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12} lg={7}>
                  <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {deptColumns.map((col) => (
                            <TableCell
                              key={col.field}
                              sx={{ fontWeight: 600 }}
                              align={col.field === 'category' ? 'left' : 'right'}
                              sortDirection={deptSortBy === col.field ? deptSortDir : false}
                            >
                              <TableSortLabel
                                active={deptSortBy === col.field}
                                direction={deptSortBy === col.field ? deptSortDir : 'asc'}
                                onClick={() => handleDeptSort(col.field)}
                              >
                                {col.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedCategoryPerformance.map((row, idx) => (
                          <TableRow key={row?.category || idx}>
                            <TableCell>{row?.category || 'Unknown'}</TableCell>
                            <TableCell align="right">{row?.total || 0}</TableCell>
                            <TableCell align="right">{row?.open || 0}</TableCell>
                            <TableCell align="right">{row?.closed || 0}</TableCell>
                            <TableCell align="right">{numberOrDash(row?.avgResolutionDays)}</TableCell>
                            <TableCell align="right">{numberOrDash(row?.avgOpenAgeDays)}</TableCell>
                            <TableCell align="right">{row?.reopens || 0}</TableCell>
                          </TableRow>
                        ))}
                        {sortedCategoryPerformance.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No data available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12} lg={5}>
                  <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, height: 480 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Top 10 Categories by Open Complaints
                    </Typography>
                    {topOpenCategories.length > 0 ? (
                      <ResponsiveContainer width="100%" height="92%">
                        <BarChart
                          data={topOpenCategories}
                          layout="vertical"
                          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                          <YAxis
                            type="category"
                            dataKey="name"
                            width={140}
                            tick={{ fontSize: 11 }}
                            interval={0}
                          />
                          <RechartsTooltip />
                          <Bar dataKey="open" name="Open" fill="#7e57c2" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                        <Typography variant="body2" color="text.secondary">
                          No data available
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* ---------------- Staff ---------------- */}
            {tab === 2 && (
              <Box>
                {renderStaffSection('Assigned Workload', staffAssigned)}
                {renderStaffSection('Registered By', staffCreated)}
              </Box>
            )}
          </>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default AnalyticsPage;
